/**
 * Daily, weekly and monthly notes.
 *
 * A periodic note is just a document whose title is a date, which is why this
 * module is mostly about naming. Two rules keep it predictable:
 *
 *  - the title pattern is fixed and sortable (ISO by default), so notes line up
 *    chronologically in any list, in any tool, on disk;
 *  - "open today" is idempotent — it opens the existing note or creates it, and
 *    never produces a second note for the same day.
 *
 * The date is formatted from the *local* calendar, not UTC: a note written at
 * 23:30 belongs to the day the writer was living in.
 */

/** Which calendar period a note covers. */
export type Period = 'daily' | 'weekly' | 'monthly';

export interface PeriodicSettings {
  /** Title pattern, in the token language documented on `formatPattern`. */
  pattern: string;
  /** Folder to create notes in; null = library root. */
  folder: string | null;
}

export const DEFAULT_SETTINGS: Record<Period, PeriodicSettings> = {
  daily: { pattern: 'YYYY-MM-DD', folder: 'Journal' },
  // GGGG, not YYYY: the ISO week-year. In a week that spans New Year — Monday
  // 28 December 2026 to Sunday 3 January 2027 is 2026-W53 — YYYY named the same
  // week "2026-W53" on Wednesday and "2027-W53" on Saturday: two notes for one
  // week, one of them for a week that does not exist.
  weekly: { pattern: 'GGGG-[W]ww', folder: 'Journal' },
  monthly: { pattern: 'YYYY-MM', folder: 'Journal' },
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0');
}

/**
 * The ISO-8601 week number, where week 1 is the one containing the first
 * Thursday. Worth getting right: the naive "day of year / 7" disagrees with
 * every calendar the user owns at the turn of the year.
 */
export function isoWeek(date: Date): { year: number; week: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // Shift to the Thursday of this week; its year is the ISO week-year.
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return { year: d.getUTCFullYear(), week };
}

/**
 * Format a date with a small, explicit token set:
 *
 *   YYYY  four-digit year        MMMM  month name      dddd  weekday name
 *   MM    two-digit month        MMM   short month     ddd   short weekday
 *   DD    two-digit day          ww    ISO week        GGGG  ISO week-year
 *
 * Text inside square brackets is literal, so `YYYY-[W]ww` gives `2026-W37`.
 */
export function formatPattern(date: Date, pattern: string): string {
  const iso = isoWeek(date);
  const replacements: [RegExp, string][] = [
    [/GGGG/g, String(iso.year)],
    [/YYYY/g, String(date.getFullYear())],
    [/MMMM/g, MONTHS[date.getMonth()] ?? ''],
    [/MMM/g, (MONTHS[date.getMonth()] ?? '').slice(0, 3)],
    [/MM/g, pad(date.getMonth() + 1)],
    [/DD/g, pad(date.getDate())],
    [/dddd/g, DAYS[date.getDay()] ?? ''],
    [/ddd/g, (DAYS[date.getDay()] ?? '').slice(0, 3)],
    [/ww/g, pad(iso.week)],
  ];

  // Literal segments are pulled out first so their letters are never treated as
  // tokens, then put back once every token has been replaced.
  const literals: string[] = [];
  let out = pattern.replace(/\[([^\]]*)\]/g, (_, text: string) => {
    literals.push(text);
    // U+0000 cannot occur in a user's pattern, so it is a collision-free
    // placeholder. Written as an escape, never as the raw byte: a literal NUL
    // made git classify this whole file as binary, which hid every diff of it.
    return `\u0000${literals.length - 1}\u0000`;
  });
  for (const [re, value] of replacements) out = out.replace(re, value);
  // eslint-disable-next-line no-control-regex -- matches the placeholder above
  return out.replace(/\u0000(\d+)\u0000/g, (_, i: string) => literals[Number(i)] ?? '');
}

/** The title a periodic note for `date` should have. */
export function periodicTitle(date: Date, settings: PeriodicSettings): string {
  return formatPattern(date, settings.pattern);
}

/** Move by whole periods, for "previous day" / "next week" navigation. */
export function shiftDate(date: Date, period: Period, delta: number): Date {
  const d = new Date(date.getTime());
  if (period === 'daily') d.setDate(d.getDate() + delta);
  else if (period === 'weekly') d.setDate(d.getDate() + delta * 7);
  else d.setMonth(d.getMonth() + delta);
  return d;
}

/** Starter content for a new periodic note. */
export function periodicTemplate(date: Date, period: Period, title: string): string {
  const heading = `# ${title}`;
  if (period === 'daily') {
    return `${heading}\n\n_${formatPattern(date, 'dddd, DD MMMM YYYY')}_\n\n## Focus\n\n- \n\n## Notes\n\n## Log\n\n`;
  }
  if (period === 'weekly') {
    return `${heading}\n\n## Highlights\n\n- \n\n## Notes\n\n## Next week\n\n- \n`;
  }
  return `${heading}\n\n## Highlights\n\n- \n\n## Review\n\n`;
}

/** Which period a date belongs to, as a comparable key. */
function periodKey(date: Date, period: Period): string {
  if (period === 'daily') return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  if (period === 'monthly') return `${date.getFullYear()}-${date.getMonth()}`;
  const { year, week } = isoWeek(date);
  return `${year}-W${week}`;
}

/**
 * Why a pattern cannot be used, or null if it can.
 *
 * "Open today" finds a note by its title, so a pattern works only if it names
 * periods, not days: every day of one period must get the same title, and
 * different periods different titles. Break the first and one week gets two
 * notes; break the second and several periods collapse into one note — `YYYY-MM`
 * for daily notes opens the same note all month, `dddd` the same note every
 * week, a pattern with no date the same note forever.
 *
 * Checked day by day across more than a year, so leap days, month ends and the
 * New Year boundary — where ISO weeks and calendar years disagree — are all in
 * range. Comparing only neighbouring periods would miss weekly and yearly repeats.
 */
export function patternProblem(
  period: Period,
  pattern: string,
): 'empty' | 'inconsistent' | 'not-unique' | null {
  if (!pattern.trim()) return 'empty';
  const titleOf = new Map<string, string>(); // period -> title
  const periodOf = new Map<string, string>(); // title -> period
  const date = new Date(2026, 10, 1);
  for (let day = 0; day < 800; day++) {
    const key = periodKey(date, period);
    const title = formatPattern(date, pattern);
    const known = titleOf.get(key);
    if (known !== undefined && known !== title) return 'inconsistent';
    const owner = periodOf.get(title);
    if (owner !== undefined && owner !== key) return 'not-unique';
    titleOf.set(key, title);
    periodOf.set(title, key);
    date.setDate(date.getDate() + 1);
  }
  return null;
}

const storageKey = (period: Period) => `markbird.periodic.${period}`;

/**
 * The pattern and folder for a period, as the user set them — or the defaults.
 * Anything stored that would not work (hand-edited storage, an older version)
 * falls back to the default rather than producing a broken note.
 */
export function loadPeriodicSettings(period: Period): PeriodicSettings {
  const fallback = DEFAULT_SETTINGS[period];
  if (typeof localStorage === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(storageKey(period));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<PeriodicSettings>;
    const pattern = typeof parsed.pattern === 'string' ? parsed.pattern : fallback.pattern;
    const folder = typeof parsed.folder === 'string' ? parsed.folder.trim() || null : null;
    return patternProblem(period, pattern) ? fallback : { pattern, folder };
  } catch {
    return fallback;
  }
}

/** Store settings for a period. Refuses a pattern `patternProblem` rejects. */
export function savePeriodicSettings(period: Period, settings: PeriodicSettings): boolean {
  if (patternProblem(period, settings.pattern)) return false;
  if (typeof localStorage === 'undefined') return false;
  localStorage.setItem(
    storageKey(period),
    JSON.stringify({ pattern: settings.pattern, folder: settings.folder?.trim() || null }),
  );
  return true;
}
